import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { agenceAPI, rapportsAPI, chatAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  House, Users, ShoppingCart, CreditCard, 
  ArrowsClockwise, ChartLine, UserCircle, SignOut,
  Factory, Receipt, UserGear, Gear, Airplane, Wallet, LockKey, EnvelopeSimple, FileText, ChatCircle, Translate, ClipboardText, AirplaneTilt, Path, List, X, Crown, MagnifyingGlass, CaretRight, CaretDown, Command
} from '@phosphor-icons/react';
import PresenceIndicator from './PresenceIndicator';
import ChatFAB from './ChatFAB';
import OnboardingWidget from './OnboardingWidget';
import TrialBanner from './TrialBanner';
import ForceChangePasswordModal from './ForceChangePasswordModal';
import SpotlightSearch from './layout/SpotlightSearch';
import ChangePasswordModal from './layout/ChangePasswordModal';
import { VoyageFiltersBadge } from '../context/VoyageContext';
import useLedgerDriftWatcher from '../hooks/useLedgerDriftWatcher';

const Layout = () => {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [agenceNom, setAgenceNom] = useState('AGENCE');
  const [nbImpayes, setNbImpayes] = useState(0);
  const [unreadChat, setUnreadChat] = useState(0);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const prevUnreadRef = useRef(0);

  const playNotifSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch {}
  }, []);

  // Refetch impayes counter on every route change so the sidebar badge stays
  // in sync after a new vente / payment / cancellation (was: fetched once at
  // mount only — caused badge to drift, e.g. show 5 while page shows 9).
  const refreshImpayes = useCallback(() => {
    rapportsAPI.getDashboard()
      .then(res => setNbImpayes(res.data?.nb_impayes || 0))
      .catch(() => {});
  }, []);

  useEffect(() => { refreshImpayes(); }, [location.pathname, refreshImpayes]);

  useEffect(() => {
    agenceAPI.getSettings().then(res => {
      if (res.data?.nom_agence) setAgenceNom(res.data.nom_agence);
    }).catch(() => {});
    chatAPI.getUnreadCount().then(res => {
      const c = res.data?.count || 0;
      prevUnreadRef.current = c;
      setUnreadChat(c);
    }).catch(() => {});
    // Poll chat unread count - tous les 30s (au lieu de 5s) et seulement
    // si l'onglet est visible. Reduit drastiquement la charge serveur quand
    // plusieurs agents/agences travaillent en simultane.
    const chatPoll = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      chatAPI.getUnreadCount().then(res => {
        const c = res.data?.count || 0;
        // Alert only if NOT on chat page and new notifications arrived
        if (c > prevUnreadRef.current && !window.location.pathname.includes('/chat')) {
          playNotifSound();
          toast.info(t('nouveau_message'), { duration: 4000 });
        }
        prevUnreadRef.current = c;
        setUnreadChat(c);
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(chatPoll);
  }, [playNotifSound, t]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isChef = user?.role === 'chef_agence';

  // Phase 2 Lot E — Vigie automatique : alerte toast si drift comptable détecté (chef only)
  useLedgerDriftWatcher(isChef);
  const autorisations = user?.autorisations || {};
  const showLabels = sidebarOpen || mobileOpen;
  const b2bVisible = isChef || autorisations.b2b_voir;

  // ===================================================================
  // SPRINT UX-2 — Navigation refondue : sections groupees + collapse
  // ===================================================================
  // Memoise les sections pliees/depliees dans localStorage
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nav_collapsed') || '{}'); }
    catch { return {}; }
  });
  const toggleSection = (key) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('nav_collapsed', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Spotlight (Ctrl+K) — recherche globale
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [spotlightQuery, setSpotlightQuery] = useState('');
  const spotlightInputRef = useRef(null);

  // Sections de navigation organisees par domaine metier
  // Refonte 16/05/2026 : suppression des doublons B2B (Ventes B2B / Rapports B2B / Voyages B2B).
  // Les filtres B2B/B2C sont desormais des onglets internes a chaque page (Ventes, Voyages, Rapports).
  const navSections = [
    {
      key: 'home',
      title: lang === 'ar' ? 'الرئيسية' : 'Accueil',
      items: [
        // Un seul Tableau de Bord adapté au rôle :
        //   - Chef d'agence → /dashboard-chef (vue agence globale)
        //   - Agent         → /dashboard (vue perso)
        // Les 2 routes restent fonctionnelles via URL directe (rétro-compat).
        isChef
          ? { path: '/dashboard-chef', icon: Crown, label: t('dashboard'), highlight: true }
          : { path: '/dashboard', icon: House, label: t('dashboard') },
      ],
    },
    {
      key: 'relations',
      title: lang === 'ar' ? 'العلاقات' : 'Relationnel',
      items: [
        { path: '/prospects', icon: ChatCircle, label: lang === 'ar' ? 'العملاء المحتملون' : 'Prospects' },
        { path: '/clients', icon: Users, label: t('clients') },
        { path: '/passagers', icon: UserCircle, label: lang === 'ar' ? 'المسافرون' : 'Passagers' },
      ],
    },
    {
      key: 'commercial',
      title: lang === 'ar' ? 'النشاط التجاري' : 'Activité commerciale',
      items: [
        { path: '/voyages', icon: Airplane, label: t('voyages') },
        { path: '/ventes', icon: ShoppingCart, label: t('ventes'), badge: nbImpayes, highlight: true },
        // Devis + Bons commande : retirés du sidebar 21/05/2026 — accessibles
        // via Ventes > onglets Devis / Bons commande (hub commercial unique).
        // Routes /devis et /bons-commande conservées pour rétro-compat.
        // Billetterie : retirée du sidebar 21/05/2026 — accessible via Rapports > onglet Billetterie.
        // /amadeus (Importer billet) : retiré aussi — accessible via bouton "+ Importer billet" dans Ventes.
        // Routes conservées pour rétro-compat.
      ],
    },
    {
      key: 'finances',
      title: lang === 'ar' ? 'المالية' : 'Finances',
      items: [
        // Refonte 21/05/2026 : point d'entrée unique fournisseurs.
        // /fournisseurs-hub, /audit/comptable-fournisseurs restent
        // accessibles via routes (rétro-compat) mais retirés du menu pour
        // éliminer les doublons. La Situation fournisseur expose tout :
        // produits, paiements, avoirs, documents, historique, audit (replié).
        // Paiements clients : REMIS DANS LE SIDEBAR 23/05/2026 (demande utilisateur)
        // pour permettre l'enregistrement des paiements client a posteriori.
        { path: '/paiements', icon: CreditCard, label: lang === 'ar' ? 'دفعات العملاء' : 'Paiements clients' },
        // Offices de paiement (V1.6 Feb 2026) : agences correspondantes qui encaissent ailleurs.
        ...(isChef || autorisations.gerer_offices ? [
          { path: '/offices', icon: Path, label: lang === 'ar' ? 'مكاتب الدفع' : 'Offices paiement' },
        ] : []),
        { path: '/fournisseurs', icon: Factory, label: t('fournisseurs'), highlight: true },
        // Créances clients : retirée du sidebar 21/05/2026 — accessible via Rapports > onglet Créances clients.
        // Route /creances-clients conservée pour rétro-compat.
        // Alertes financières : retirée du sidebar 21/05/2026 — accessible via Rapports > onglet Alertes financières.
        // Route /alertes-financieres conservée pour rétro-compat.
      ],
    },
    // Operations voyage : retirées du sidebar 21/05/2026 — accès via la
    // VoyageCard (Mes Voyages → boutons Suivi voyage / Mode Guide).
    // Routes /suivi-voyage et /guide conservées pour rétro-compat.
    {
      key: 'analytics',
      title: lang === 'ar' ? 'التحليل' : 'Analyse',
      items: [
        ...(isChef || autorisations.voir_rapports ? [{ path: '/rapports', icon: ChartLine, label: t('rapports') }] : []),
        ...(isChef ? [{ path: '/comptabilite', icon: ClipboardText, label: 'Comptabilité' }] : []),
        { path: '/charges', icon: Receipt, label: lang === 'ar' ? 'المصاريف والتكاليف' : 'Charges & Dépenses' },
        // Historique emails : retiré du sidebar 21/05/2026 — accessible via Rapports > onglet Historique emails.
        // Route /historique-emails conservée pour rétro-compat.
        // Chat équipe : retiré du sidebar 21/05/2026 — accessible via le badge
        // "X en ligne" en haut à droite (PresenceIndicator > Ouvrir le Chat équipe).
        // Route /chat conservée pour rétro-compat.
      ],
    },
    {
      key: 'admin',
      title: lang === 'ar' ? 'الإدارة' : 'Administration',
      items: [
        // Agents : retiré du sidebar 21/05/2026 — accessible via Paramètres > onglet Agents.
        // Route /agents conservée pour rétro-compat.
        ...(isChef || autorisations.modifier_parametres ? [{ path: '/parametres', icon: Gear, label: t('parametres'), highlight: true }] : []),
        // Retiré du sidebar 21/05/2026 — accessible via Paramètres > onglets :
        //   /audit-logs → Paramètres ?tab=audit
        //   /billing → Paramètres ?tab=abonnement
        //   /marketplace → Paramètres ?tab=modules
        //   /agents → Paramètres ?tab=agents
        // Routes conservées pour rétro-compat (URLs directes fonctionnent).
      ],
    },
  ].filter(s => s.items.length > 0);

  // ===================================================================
  // Spotlight Ctrl+K - keyboard shortcut + items index
  // ===================================================================
  const allFlatItems = navSections.flatMap(s => s.items);
  const spotlightFiltered = spotlightQuery.trim()
    ? allFlatItems.filter(it =>
        (it.label || '').toLowerCase().includes(spotlightQuery.toLowerCase()) ||
        (it.path || '').toLowerCase().includes(spotlightQuery.toLowerCase())
      ).slice(0, 8)
    : allFlatItems.slice(0, 8);

  useEffect(() => {
    const handler = (e) => {
      // Ctrl+K ou Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSpotlightOpen(true);
        setSpotlightQuery('');
        setTimeout(() => spotlightInputRef.current?.focus(), 50);
      } else if (e.key === 'Escape' && spotlightOpen) {
        setSpotlightOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [spotlightOpen]);

  const goToSpotlight = (path) => {
    setSpotlightOpen(false);
    setSpotlightQuery('');
    navigate(path);
  };


  return (
    <div className="flex h-screen bg-stone-50">
      {/* Force change password modal (chef after temp pwd reset) */}
      <ForceChangePasswordModal lang={lang} />

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside className={`bg-slate-900 border-e border-slate-800 transition-all duration-300 fixed md:static inset-y-0 left-0 z-50 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0 w-64 ${sidebarOpen ? 'md:w-64' : 'md:w-20'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{
                  background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-primary-dark) 100%)',
                  boxShadow: '0 4px 14px 0 rgba(var(--theme-primary-rgb), 0.4)',
                }}
              >
                <Airplane size={18} weight="fill" className="text-white -rotate-12" />
              </div>
              {showLabels && (
                <div className="min-w-0 flex-1">
                  <h1 className="font-display font-bold text-lg tracking-tight text-white truncate" data-testid="sidebar-agence-nom">
                    {agenceNom}
                  </h1>
                  {user?.nom && (
                    <p className="text-xs text-slate-400 truncate" data-testid="sidebar-user-name">
                      {user.nom}
                    </p>
                  )}
                </div>
              )}
              {/* Mobile close button */}
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="md:hidden text-slate-300 hover:text-white p-1 -mr-1"
                data-testid="mobile-sidebar-close"
                aria-label="Fermer menu"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Quick search trigger (Ctrl+K) - visible quand sidebar ouverte */}
          {showLabels && (
            <div className="px-3 pt-3">
              <button
                type="button"
                onClick={() => { setSpotlightOpen(true); setSpotlightQuery(''); setTimeout(() => spotlightInputRef.current?.focus(), 50); }}
                data-testid="sidebar-spotlight-trigger"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/70 hover:bg-slate-800 text-slate-400 hover:text-white text-sm transition-colors"
              >
                <MagnifyingGlass size={16} />
                <span className="flex-1 text-left">{lang === 'ar' ? 'بحث...' : 'Recherche...'}</span>
                <kbd className="hidden md:inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 border border-slate-600">Ctrl K</kbd>
              </button>
            </div>
          )}

          {/* Navigation - sections groupees */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
            {navSections.map((section) => {
              const isCollapsed = collapsedSections[section.key];
              return (
                <div key={section.key} className="mb-1">
                  {showLabels && (
                    <button
                      type="button"
                      onClick={() => toggleSection(section.key)}
                      data-testid={`nav-section-toggle-${section.key}`}
                      className="w-full flex items-center gap-1.5 px-3 pt-3 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {isCollapsed ? <CaretRight size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
                      <span>{section.title}</span>
                    </button>
                  )}
                  {!showLabels && navSections.indexOf(section) > 0 && (
                    <div className="border-t border-slate-800 my-2" />
                  )}
                  {(!isCollapsed || !showLabels) && (
                    <div className="space-y-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path.includes('?') || ['/voyages', '/ventes', '/rapports'].includes(item.path)}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={({ isActive }) =>
                              `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                                isActive
                                  ? 'text-white shadow-lg'
                                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                              }`
                            }
                            style={({ isActive }) => isActive ? {
                              background: 'linear-gradient(90deg, var(--theme-primary) 0%, var(--theme-primary-dark) 100%)',
                              boxShadow: '0 8px 20px -6px rgba(var(--theme-primary-rgb), 0.5)',
                            } : undefined}
                          >
                            {({ isActive }) => (
                              <>
                                <Icon size={20} weight={isActive ? 'fill' : 'regular'} className="shrink-0" />
                                {showLabels && (
                                  <span className="font-medium text-sm flex items-center gap-2 flex-1 min-w-0">
                                    <span className="truncate">{item.label}</span>
                                    {item.badge > 0 && (
                                      <span
                                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${isActive ? 'bg-white' : 'bg-orange-500 text-white'}`}
                                        style={isActive ? { color: 'var(--theme-primary-dark)' } : undefined}
                                        data-testid={`badge-${item.path.replace(/[^a-z0-9]/gi, '-')}`}
                                      >
                                        {item.badge}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User Info bloc retiré 21/05/2026 — nom utilisateur déjà affiché sous le logo VIANOSTRA en haut. */}
          {/* Aide retirée du footer 21/05/2026 — pas nécessaire pour l'instant. */}
          <div className="p-3 border-t border-slate-800">
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 hover:text-red-200 transition-colors text-sm font-semibold"
            >
              <SignOut size={16} />
              {showLabels && <span>{t('deconnexion')}</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Trial / Subscription Banner */}
        <TrialBanner />
        {/* Top Bar */}
        <header className="bg-white/80 backdrop-blur-md border-b border-stone-100 px-4 md:px-8 py-3 md:py-4 z-30 sticky top-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="md:hidden p-2 -ml-2 rounded-lg text-stone-700 hover:bg-stone-100"
                data-testid="mobile-sidebar-open"
                aria-label="Ouvrir menu"
              >
                <List size={22} />
              </button>
              <div className="min-w-0">
                <h2 className="font-display text-lg md:text-2xl font-bold tracking-tight text-stone-900 truncate">
                  {lang === 'ar' ? 'إدارة وكالة السفر' : 'Gestion Agence de Voyage'}
                </h2>
                <p className="hidden sm:block text-xs text-stone-500 mt-0.5">
                  {new Date().toLocaleDateString(lang === 'ar' ? 'ar-DZ' : 'fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <VoyageFiltersBadge />
              <PresenceIndicator unreadChat={unreadChat} />
              <button
                onClick={toggleLang}
                data-testid="lang-toggle-btn"
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 text-sm font-medium text-stone-700 transition-colors"
              >
                <Translate size={16} />
                <span className="hidden sm:inline">{lang === 'fr' ? 'العربية' : 'Français'}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 md:p-8 app-main-scroll">
          <Outlet />
        </main>
      </div>

      {/* Onboarding Widget (visible tant que toutes les etapes ne sont pas faites) */}
      <OnboardingWidget />

      {/* FAB Chat équipe (visible partout sauf /chat lui-même) */}
      <ChatFAB unreadChat={unreadChat} />

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={showPwdModal}
        onClose={() => setShowPwdModal(false)}
        lang={lang}
        t={t}
      />

      {/* Spotlight Search (Ctrl+K) */}
      <SpotlightSearch
        open={spotlightOpen}
        onClose={() => setSpotlightOpen(false)}
        query={spotlightQuery}
        onQueryChange={setSpotlightQuery}
        results={spotlightFiltered}
        onGo={goToSpotlight}
        inputRef={spotlightInputRef}
        lang={lang}
      />
    </div>
  );
};

export default Layout;